import { AlertTriangle, type LucideIcon } from "lucide-react";
import React from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  pendingLabel?: string;
  /** Style the confirm button as a destructive action and show a warning icon. */
  destructive?: boolean;
  /** Optional header icon. Defaults to a warning triangle for destructive dialogs. */
  icon?: LucideIcon;
  /**
   * Confirm handler. May return a promise — while it is pending the dialog is
   * locked (no ESC / outside-click / button presses) and the buttons show a
   * busy label. If it rejects, the error is shown inline and the dialog stays
   * open so the user can retry.
   */
  onConfirm: () => void | Promise<void>;
}

/**
 * ConfirmDialog
 *
 * A reusable confirmation dialog built on Radix `AlertDialog` (correct
 * semantics + focus handling for confirmations). It owns its own pending and
 * error state so callers only provide an async `onConfirm`.
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  pendingLabel,
  destructive = false,
  icon,
  onConfirm,
}) => {
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Clear any stale error whenever the dialog is (re)opened.
  React.useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const Icon = icon ?? (destructive ? AlertTriangle : undefined);

  const handleConfirm = async () => {
    setPending(true);
    setError(null);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (e: any) {
      setError(e?.message || "Something went wrong. Please try again.");
    } finally {
      setPending(false);
    }
  };

  const handleOpenChange = (next: boolean) => {
    // Don't allow closing while an action is in flight.
    if (pending) return;
    onOpenChange(next);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent
        className="sm:max-w-[500px]"
        onEscapeKeyDown={(e) => {
          // Radix AlertDialog already ignores outside-clicks for dismissal, so
          // we only need to block ESC while an action is in flight.
          if (pending) e.preventDefault();
        }}
      >
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            {Icon && (
              <div
                className={
                  destructive
                    ? "rounded-full bg-destructive/10 p-2"
                    : "rounded-full bg-muted p-2"
                }
              >
                <Icon
                  className={
                    destructive
                      ? "h-5 w-5 text-destructive"
                      : "h-5 w-5 text-foreground"
                  }
                />
              </div>
            )}
            <AlertDialogTitle>{title}</AlertDialogTitle>
          </div>
          {description && (
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-3 text-sm text-muted-foreground">
                {description}
              </div>
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>

        {error && (
          <div
            role="alert"
            className="rounded-md border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </div>
        )}

        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={pending}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? "destructive" : "default"}
            onClick={handleConfirm}
            disabled={pending}
          >
            {pending ? (pendingLabel ?? "Working…") : confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmDialog;
